import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row, Col, Card, Statistic, Spin, Select, Space, DatePicker, Table, Alert, Tag, Segmented,
  Tooltip as AntTooltip, Button, Empty,
} from 'antd';
import { ThunderboltOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

/**
 * The three measures, in the order they are read.
 *
 * `dispatched` is the one that settles; the others say how much capacity was
 * standing behind it. Keeping them in one table lets the page label every
 * chart and card from the same source rather than repeating strings.
 *
 * Registered capacity used to sit at the front of this list. It came from
 * DUDETAIL, which is not maintained, so it has been dropped rather than shown
 * with a caveat.
 */
const MEASURES = [
  { key: 'availableMw', label: 'Available', short: 'Avail', colour: '#54A2CC',
    help: 'What AEMO assessed as deliverable each interval, after derating for energy target, ramp rate and enablement limits.' },
  { key: 'offeredMw', label: 'Bidding', short: 'Bid', colour: '#d48806',
    help: 'MAXAVAIL bid into the service. Regularly exceeds Available, because the bid is not derated.' },
  { key: 'dispatchedMw', label: 'Dispatch', short: 'Disp', colour: '#389e0d',
    help: 'What AEMO actually enabled. This is the figure that settles.' },
];

/**
 * A colour per plant type, fixed to the type rather than to its rank.
 *
 * Storage and aggregation share a blue-green family because they behave alike
 * in this market; thermal plant sits in warm tones. A filter that drops a type
 * never recolours the ones left behind.
 */
const CATEGORY_COLOURS = {
  utility_bess: '#54A2CC',
  vpp_bess: '#13c2c2',
  vpp_dr: '#08979c',
  hydro: '#597ef7',
  coal: '#5c4033',
  gas: '#d48806',
  wind: '#389e0d',
  solar: '#fadb14',
  biomass: '#a0d911',
  liquid: '#fa541c',
  unclassified: '#bfbfbf',
};

// A distinct hue per slice, assigned in fixed order so a filter that removes a
// participant never repaints the ones that remain.
const SLICE_COLOURS = [
  '#54A2CC', '#389e0d', '#d48806', '#9254de', '#13c2c2',
  '#eb2f96', '#597ef7', '#a0d911', '#fa8c16', '#f5222d',
  '#08979c', '#722ed1',
];

const fmtMw = (v) => (v == null ? '—' : `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })}`);
const fmtPct = (v) => (v == null ? '—' : `${Math.round(Number(v) * 100)}%`);

export default function FcasMarket() {
  const [meta, setMeta] = useState(null);
  const [date, setDate] = useState(null);
  /**
   * The page opens on the view it is usually opened for: behind-the-meter
   * battery aggregation in the fastest contingency service. Every filter is
   * still cleared with one click, and clearing them shows the whole market.
   */
  const [services, setServices] = useState(['RAISE1SEC']);
  const [regions, setRegions] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [categories, setCategories] = useState(['vpp_bess']);

  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState(null);

  // Which measure the pie and the ranking bar are drawn from.
  const [measure, setMeasure] = useState('dispatchedMw');
  // What the pie slices are cut by.
  const [sliceBy, setSliceBy] = useState('participant');

  // ---- filter vocabulary, fetched once
  useEffect(() => {
    let live = true;
    api.get('/api/fcas-market/meta')
      .then((res) => {
        if (!live) return;
        const d = res.data.data;
        setMeta(d);
        setDate(dayjs(d.defaultDate));
      })
      .catch((err) => setError(err.response?.data?.message || 'Could not load filter options'));
    return () => { live = false; };
  }, []);

  const params = useMemo(() => {
    if (!date) return null;
    const p = { date: date.format('YYYY-MM-DD') };
    if (services.length) p.services = services.join(',');
    if (regions.length) p.regions = regions.join(',');
    if (participants.length) p.participants = participants.join(',');
    if (categories.length) p.categories = categories.join(',');
    return p;
  }, [date, services, regions, participants, categories]);

  const load = useCallback(async () => {
    if (!params) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/fcas-market/summary', { params });
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load FCAS market data');
      setData(null);
    }
    setLoading(false);
  }, [params]);

  useEffect(() => { load(); }, [load]);

  // The interval profile is a second query and is only worth running once the
  // summary has told us there is something to draw.
  useEffect(() => {
    if (!params || !data || !data.rows.length) { setProfile(null); return; }
    let live = true;
    setProfileLoading(true);
    api.get('/api/fcas-market/profile', { params })
      .then((res) => { if (live) setProfile(res.data.data); })
      .catch(() => { if (live) setProfile(null); })
      .finally(() => { if (live) setProfileLoading(false); });
    return () => { live = false; };
  }, [params, data]);

  const measureMeta = MEASURES.find((m) => m.key === measure) || MEASURES[3];

  /**
   * Pie slices: the selected measure, grouped, largest first.
   *
   * Everything past the eleventh becomes one "Other" slice — a pie with sixty
   * wedges communicates nothing, and the table below carries the detail.
   */
  const slices = useMemo(() => {
    if (!data?.rows?.length) return [];
    const byKey = new Map();
    const labelOf = new Map((data.byCategory || []).map((c) => [c.category, c.label]));
    for (const r of data.rows) {
      const key = sliceBy === 'participant' ? (r.participant || 'Unknown')
        : sliceBy === 'region' ? (r.region || 'Unknown')
          : sliceBy === 'category' ? (labelOf.get(r.category) || 'Unclassified')
            : r.duid;
      byKey.set(key, (byKey.get(key) || 0) + (Number(r[measure]) || 0));
    }
    const all = [...byKey.entries()]
      .map(([name, value]) => ({ name, value: Math.round(value * 10) / 10 }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value);
    if (all.length <= 12) return all;
    const head = all.slice(0, 11);
    const tail = all.slice(11).reduce((t, x) => t + x.value, 0);
    return [...head, { name: `Other (${all.length - 11})`, value: Math.round(tail * 10) / 10 }];
  }, [data, measure, sliceBy]);

  const sliceTotal = useMemo(() => slices.reduce((t, x) => t + x.value, 0), [slices]);
  const categoryKeyByLabel = useMemo(
    () => new Map((data?.byCategory || []).map((c) => [c.label, c.category])),
    [data],
  );

  // Per-service comparison, for the bar chart.
  const serviceBars = useMemo(() => (data?.byService || []).map((s) => ({
    name: s.label,
    Available: s.availableMw,
    Bidding: s.offeredMw,
    Dispatch: s.dispatchedMw,
  })), [data]);

  const categoryLabel = useMemo(
    () => new Map((meta?.categories || []).map((c) => [c.key, c.label])),
    [meta],
  );

  const columns = [
    { title: 'DUID', dataIndex: 'duid', width: 120, fixed: 'left',
      sorter: (a, b) => a.duid.localeCompare(b.duid) },
    { title: 'Participant', dataIndex: 'participant', width: 130,
      sorter: (a, b) => (a.participant || '').localeCompare(b.participant || '') },
    { title: 'Region', dataIndex: 'region', width: 90,
      render: (v) => (v ? <Tag>{v}</Tag> : '—'),
      sorter: (a, b) => (a.region || '').localeCompare(b.region || '') },
    {
      title: 'Type', dataIndex: 'category', width: 170,
      render: (v, row) => {
        const label = categoryLabel.get(v) || v;
        const colour = CATEGORY_COLOURS[v] || '#bfbfbf';
        // Say what the label rests on: a registered fuel, or the behaviour the
        // unit showed over the trailing window.
        const why = row.fuel
          ? `AEMO records this unit's energy source as ${row.fuel}.`
          : 'AEMO records no fuel for this unit. It is typed from its dispatch shape: '
            + 'a non-scheduled aggregation that is ever enabled to lower must be storage, '
            + 'while one that only ever raises is curtailable load.';
        return (
          <AntTooltip title={why}>
            <Tag color={colour} style={{ borderColor: colour }}>{label}</Tag>
          </AntTooltip>
        );
      },
      sorter: (a, b) => (categoryLabel.get(a.category) || '').localeCompare(categoryLabel.get(b.category) || ''),
    },
    ...MEASURES.map((m) => ({
      title: (
        <AntTooltip title={m.help}>
          <span style={{ borderBottom: '1px dotted #bbb' }}>{m.label} (MW)</span>
        </AntTooltip>
      ),
      dataIndex: m.key,
      width: 130,
      align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMw(v)}</span>,
      sorter: (a, b) => (Number(a[m.key]) || 0) - (Number(b[m.key]) || 0),
      defaultSortOrder: m.key === 'dispatchedMw' ? 'descend' : undefined,
    })),
    {
      title: (
        <AntTooltip title="The most this unit was enabled for on any single service, at any interval in the day. Unlike the columns to its left this is not summed across services, so it is the only physical megawatt figure in the row.">
          <span style={{ borderBottom: '1px dotted #bbb' }}>Peak 1-svc (MW)</span>
        </AntTooltip>
      ),
      dataIndex: 'peakServiceMw', width: 130, align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMw(v)}</span>,
      sorter: (a, b) => (a.peakServiceMw || 0) - (b.peakServiceMw || 0),
    },
    {
      title: (
        <AntTooltip title="Bid as a share of what AEMO assessed as available. Above 100% is ordinary — the bid is not derated.">
          <span style={{ borderBottom: '1px dotted #bbb' }}>Bid / Avail</span>
        </AntTooltip>
      ),
      dataIndex: 'offerRate', width: 110, align: 'right',
      render: (v) => fmtPct(v),
      sorter: (a, b) => (a.offerRate || 0) - (b.offerRate || 0),
    },
    {
      title: (
        <AntTooltip title="Dispatched as a share of what was bid — how much of the offer the market actually took.">
          <span style={{ borderBottom: '1px dotted #bbb' }}>Clear rate</span>
        </AntTooltip>
      ),
      dataIndex: 'clearRate', width: 110, align: 'right',
      render: (v) => (v == null ? '—' : (
        <span style={{ color: v >= 0.5 ? '#389e0d' : v >= 0.2 ? '#d48806' : '#8c8c8c' }}>{fmtPct(v)}</span>
      )),
      sorter: (a, b) => (a.clearRate || 0) - (b.clearRate || 0),
    },
  ];

  const allServices = meta?.services || [];
  const serviceOptions = ['Raise', 'Lower'].map((dir) => ({
    label: dir,
    options: allServices.filter((s) => s.direction === dir)
      .map((s) => ({ label: s.label, value: s.key })),
  }));

  return (
    <PageLayout>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>
          <ThunderboltOutlined style={{ color: '#54A2CC', marginRight: 8 }} />
          FCAS Market
        </h2>
        <div style={{ color: '#8c8c8c', fontSize: 13 }}>
          Participation, bidding and dispatch by DUID — AEMO DISPATCHLOAD and BIDPEROFFER_D
        </div>
      </div>

      {/* ---------------------------------------------------------- filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size={[12, 12]}>
          <Space size={4}>
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>Day</span>
            <DatePicker
              value={date}
              onChange={(v) => v && setDate(v)}
              allowClear={false}
              // One day at a time: seven days of these tables takes half a
              // minute, so the page trades range for an answer that arrives.
              disabledDate={(d) => d && d > dayjs().endOf('day')}
            />
          </Space>
          <Select
            mode="multiple" allowClear placeholder="All services"
            style={{ minWidth: 260 }} maxTagCount={2}
            value={services} onChange={setServices} options={serviceOptions}
          />
          <Select
            mode="multiple" allowClear placeholder="All regions"
            style={{ minWidth: 180 }} maxTagCount={3}
            value={regions} onChange={setRegions}
            options={(meta?.regions || []).map((r) => ({ label: r, value: r }))}
          />
          <Select
            mode="multiple" allowClear placeholder="All types"
            style={{ minWidth: 220 }} maxTagCount={1}
            value={categories} onChange={setCategories}
            options={(meta?.categories || []).map((c) => ({ label: c.label, value: c.key }))}
          />
          <Select
            mode="multiple" allowClear showSearch placeholder="All participants"
            style={{ minWidth: 220 }} maxTagCount={1}
            value={participants} onChange={setParticipants}
            options={(meta?.participants || []).map((p) => ({ label: p, value: p }))}
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      </Card>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      {(data?.warnings || []).map((w) => (
        <Alert key={w} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />
      ))}

      <Spin spinning={loading}>
        {/* ------------------------------------------------------ score cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {MEASURES.map((m) => (
            <Col xs={24} md={8} key={m.key}>
              <Card size="small">
                <AntTooltip title={m.help}>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>
                    {m.label} <InfoCircleOutlined style={{ fontSize: 10 }} />
                  </div>
                </AntTooltip>
                <Statistic
                  value={data?.totals?.[m.key] ?? 0}
                  precision={1}
                  suffix="MW"
                  valueStyle={{ color: m.colour, fontSize: 24 }}
                />
                <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                  average over {data?.intervals ?? 0} intervals
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={6}>
            <Card size="small">
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>In the market</div>
              <Statistic value={data?.totals?.duids ?? 0} suffix="units"
                valueStyle={{ fontSize: 20 }} />
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                across {data?.totals?.participants ?? 0} participants
              </div>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card size="small">
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Bid / Available</div>
              <Statistic value={(data?.totals?.offerRate ?? 0) * 100} precision={0} suffix="%"
                valueStyle={{ fontSize: 20, color: '#d48806' }} />
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>above 100% is ordinary — bids are not derated</div>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card size="small">
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Clear rate (Dispatch / Bid)</div>
              <Statistic value={(data?.totals?.clearRate ?? 0) * 100} precision={0} suffix="%"
                valueStyle={{ fontSize: 20, color: '#389e0d' }} />
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>share of offered capacity the market took</div>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card size="small">
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Dispatched energy-equivalent</div>
              <Statistic value={data?.totals?.dispatchedMwh ?? 0} precision={0} suffix="MWh"
                valueStyle={{ fontSize: 20 }} />
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>enablement MW summed over the day</div>
            </Card>
          </Col>
        </Row>

        {/* ------------------------------------------------ composition + mix */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={10}>
            <Card
              size="small"
              title={`${measureMeta.label} share`}
              extra={(
                <Space size={4}>
                  <Segmented size="small" value={sliceBy} onChange={setSliceBy}
                    options={[
                      { label: 'Type', value: 'category' },
                      { label: 'Participant', value: 'participant' },
                      { label: 'Region', value: 'region' },
                      { label: 'DUID', value: 'duid' },
                    ]} />
                </Space>
              )}
            >
              <Segmented
                size="small" block value={measure} onChange={setMeasure}
                style={{ marginBottom: 8 }}
                options={MEASURES.map((m) => ({ label: m.short, value: m.key }))}
              />
              {slices.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={slices} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      outerRadius={95} innerRadius={45} paddingAngle={2}>
                      {slices.map((s, i) => (
                        <Cell key={s.name}
                          fill={sliceBy === 'category'
                            ? (CATEGORY_COLOURS[categoryKeyByLabel.get(s.name)] || '#bfbfbf')
                            : SLICE_COLOURS[i % SLICE_COLOURS.length]}
                          stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [
                      `${fmtMw(v)} MW (${sliceTotal ? Math.round((v / sliceTotal) * 100) : 0}%)`, n,
                    ]} />
                    <Legend verticalAlign="bottom" height={60} iconType="circle"
                      wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Empty description="Nothing in this selection" />}
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card size="small" title="By service — available, bid and dispatched">
              {serviceBars.length ? (
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={serviceBars} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35}
                      textAnchor="end" interval={0} height={60} />
                    <YAxis tick={{ fontSize: 11 }} label={{ value: 'MW', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                    <Tooltip formatter={(v, n) => [`${fmtMw(v)} MW`, n]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Available" fill="#54A2CC" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Bidding" fill="#d48806" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Dispatch" fill="#389e0d" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty description="Nothing in this selection" />}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24}>
            <Card size="small" title="By plant type">
              {(data?.byCategory || []).length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 4 }}>
                  {data.byCategory.map((c) => {
                    const share = data.totals.dispatchedMw > 0
                      ? c.dispatchedMw / data.totals.dispatchedMw : 0;
                    return (
                      <div key={c.category} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 150, fontSize: 12 }}>{c.label}</span>
                        <span style={{ width: 52, fontSize: 11, color: '#8c8c8c', textAlign: 'right' }}>
                          {c.duids} units
                        </span>
                        <div style={{ flex: 1, background: '#f5f5f5', height: 14, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            width: `${Math.max(share * 100, share > 0 ? 1 : 0)}%`,
                            background: CATEGORY_COLOURS[c.category] || '#bfbfbf',
                            height: '100%',
                          }}
                          />
                        </div>
                        <span style={{ width: 96, fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtMw(c.dispatchedMw)} MW
                        </span>
                        <span style={{ width: 42, fontSize: 11, color: '#8c8c8c', textAlign: 'right' }}>
                          {Math.round(share * 100)}%
                        </span>
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                    Dispatch MW and share of the selection.
                  </div>
                </div>
              ) : <Empty description="Nothing in this selection" />}
            </Card>
          </Col>
        </Row>

        {/* ------------------------------------------------------ over the day */}
        <Card size="small" title="Across the day (all selected units summed)" style={{ marginBottom: 16 }}>
          <Spin spinning={profileLoading}>
            {profile?.series?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={profile.series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={35} />
                  <YAxis tick={{ fontSize: 11 }} label={{ value: 'MW', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                  <Tooltip formatter={(v, n) => [`${fmtMw(v)} MW`, n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="availableMw" name="Available" stroke="#54A2CC" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="offeredMw" name="Bidding" stroke="#d48806" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="dispatchedMw" name="Dispatch" stroke="#389e0d" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : <Empty description={profileLoading ? ' ' : 'No interval data'} />}
          </Spin>
        </Card>

        {/* ------------------------------------------------------------ table */}
        <Card
          size="small"
          title={`Units — ${data?.rows?.length ?? 0}`}
          extra={data && (
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
              {data.date} · {data.intervals} intervals · {data.services.length} services
            </span>
          )}
        >
          <Table
            rowKey="duid"
            size="small"
            dataSource={data?.rows || []}
            columns={columns}
            scroll={{ x: 1180 }}
            pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} units` }}
          />
          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 8 }}>
            Available, Bidding and Dispatch are averages over the day’s dispatch intervals. With
            more than one service selected they double count physical capacity by design — a battery
            offers the same megawatts into 6s, 60s and 5min at once and can be enabled for all three
            — so an all-service total measures market participation, not plant rating.
            <b> Peak 1-svc</b> is the exception: the largest enablement on any single service, and
            the only physical megawatt figure in the row.
          </div>
        </Card>
      </Spin>
    </PageLayout>
  );
}
